import { AudioLines, Box, Mic, Palette, ScanLine, Type } from "@suite/components/icons";
import { Card } from "@suite/components/ui/card";
import { cn } from "@suite/lib/utils";

/* The scan state of the fingerprint instrument.
 *
 * Deliberately NOT a generic spinner: it is the report's own chassis with the measured values
 * still missing. Everything knowable before the scan runs — the card's split ledger, the
 * snapshot quadrant, the three verdict columns, the five vector chips, the provenance line —
 * is rendered for real at its final size, so when the report lands the values fill into
 * slots that already exist instead of replacing a small box with a tall one.
 *
 * It carries no client state, so the route-level loading.tsx (a Server Component) and the
 * tool's own pre-report branch can render the exact same markup. From the visitor's point of
 * view that collapses three states — route spinner, scan card, report — into one.
 */

/* Microtype scales copied from the report card (HEADER / VERDICT_EYEBROW in
 * BrowserFingerprint) so skeleton and result share one typographic grid: no label shifts
 * when the values arrive. */
const HEADER = "font-mono text-[9px] font-semibold tracking-widest text-white/70 uppercase";
const EYEBROW = "font-mono text-[10px] font-semibold tracking-widest text-white/70 uppercase";

/** What the collector works through, in order. The one part of this screen that is honestly
 * about waiting, so it keeps the pulse. */
const SCAN_STEPS = [
  "Browser & platform",
  "Client Hints",
  "Screen & display",
  "Hardware",
  "Canvas render",
  "WebGL profile",
  "Audio engine",
  "Fonts & voices",
  "Media & storage",
];

/** The five vectors are fixed before any measurement, so their chips render for real —
 * dimmed, in final position — and the scan only adds each outcome word. The row is laid out
 * once and never reflows. */
const VECTORS = [
  { key: "canvas", label: "Draws", icon: Palette },
  { key: "webgl", label: "Graphics", icon: Box },
  { key: "audio", label: "Sound", icon: AudioLines },
  { key: "fonts", label: "Fonts", icon: Type },
  { key: "voices", label: "Voices", icon: Mic },
];

/** Hex groups of the pending hash: eight blanks sized like the real HashDisplay's groups, so
 * the quadrant holds its height and wrap when the digits land. */
const HASH_GROUPS = ["h1", "h2", "h3", "h4", "h5", "h6", "h7", "h8"];

/** Ghost rows for the two cards below the instrument (consistency checks, property
 * inspector). They exist to hold the page's height: without them the footer rides up under
 * the sub-nav and gets shoved down a full screen when the report arrives. */
const CHECK_ROWS = ["c1", "c2", "c3", "c4", "c5", "c6"];
const PROPERTY_ROWS = ["p1", "p2", "p3", "p4", "p5", "p6"];

interface BarProps {
  className?: string;
}

/** A value that hasn't arrived yet. Low-contrast on purpose — it should read as an empty slot in
 * an instrument, not as content.
 *
 * `skeleton-bar` (globals.css) travels a soft highlight across the slot. It shimmers rather than
 * pulses: opacity pulsing makes the swap to the report catch elements mid-fade, and that
 * brightness step is what reads as a flash. Here the element's own brightness barely moves, so
 * it can stop at any point in its cycle unnoticed. */
function Bar({ className }: BarProps) {
  return <span aria-hidden className={cn("skeleton-bar block rounded-full", className)} />;
}

const GLYPH_GRID = 7;
const GLYPH_CELL = 10;
const GLYPH_GAP = 3;
const GLYPH_SIZE = GLYPH_GRID * GLYPH_CELL + (GLYPH_GRID - 1) * GLYPH_GAP;

/** The identicon before it has a hash to draw: the same 7×7 geometry as FingerprintGlyph, every
 * cell at one dim level, so the real pattern resolves into a grid the eye has already accepted
 * and no cell is caught mid-fade when it does. */
function GlyphGhost() {
  const cells = [];
  for (let row = 0; row < GLYPH_GRID; row++) {
    for (let col = 0; col < GLYPH_GRID; col++) {
      cells.push({ row, col });
    }
  }

  return (
    <svg
      viewBox={`0 0 ${GLYPH_SIZE} ${GLYPH_SIZE}`}
      className="text-brand-green size-14"
      aria-hidden
    >
      {cells.map(({ row, col }) => (
        <rect
          key={`${row}-${col}`}
          x={col * (GLYPH_CELL + GLYPH_GAP)}
          y={row * (GLYPH_CELL + GLYPH_GAP)}
          width={GLYPH_CELL}
          height={GLYPH_CELL}
          rx={2.5}
          fill="currentColor"
          opacity={0.14}
        />
      ))}
    </svg>
  );
}

/** Corner marks around the glyph — the same bracket language the report uses, so the frame
 * is already drawn when the pattern appears. */
function CornerBrackets() {
  return (
    <>
      <span
        className="border-brand-green/50 absolute top-0 left-0 size-2.5 border-t border-l"
        aria-hidden
      />
      <span
        className="border-brand-green/50 absolute top-0 right-0 size-2.5 border-t border-r"
        aria-hidden
      />
      <span
        className="border-brand-green/50 absolute bottom-0 left-0 size-2.5 border-b border-l"
        aria-hidden
      />
      <span
        className="border-brand-green/50 absolute right-0 bottom-0 size-2.5 border-r border-b"
        aria-hidden
      />
    </>
  );
}

export default function FingerprintScanning() {
  return (
    /* aria-busy + one polite status line: the sr-only text is the only thing a screen reader
     * needs from this screen, and BrowserFingerprint owns the same announcement, so nothing
     * is announced twice when it takes over. */
    <div className="flex flex-col gap-5" aria-busy="true">
      <Card className="@container relative gap-0 overflow-hidden p-0">
        {/* Only the corner bloom, exactly as the finished card has it, so the lighting is
         * unchanged when the values land. No travelling scan line: the motion belongs to the
         * empty value slots, not to a decoration laid over the whole instrument. */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
          <div className="bg-brand-green/[0.06] absolute -top-24 -right-16 size-72 rounded-full blur-3xl" />
        </div>

        {/* Split ledger — identical grid to the report: verdict on the left, snapshot
         * quadrant on the right. */}
        <div className="relative flex flex-col @4xl:grid @4xl:grid-cols-3">
          <div className="flex min-w-0 flex-col justify-center gap-2 p-5 @4xl:col-span-2 @4xl:max-w-3xl @4xl:py-7 @4xl:pr-10">
            <div className="flex items-center gap-2.5">
              <ScanLine className="text-brand-green size-4" aria-hidden />
              {/* Sits at the report headline's size so the verdict replaces it in place. */}
              <p className="text-2xl font-semibold tracking-tight text-white sm:text-[26px]">
                Reading your browser&apos;s signals…
              </p>
            </div>
            {/* The step list stands in for the report's gloss paragraph, at roughly its height.
             * No extra line above it: every line here that the report doesn't have is a height
             * the card has to give back when the values land. */}
            <div className="mt-2 grid max-w-2xl grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3">
              {/* Steady, not blinking. These labels sit where the report's gloss paragraph
               * lands, so anything oscillating here is a brightness step at the swap. */}
              {SCAN_STEPS.map((step) => (
                <span
                  key={step}
                  className="flex items-center gap-1.5 font-mono text-[10px] tracking-widest text-white/60 uppercase"
                >
                  <span className="bg-brand-green/50 size-1 shrink-0 rounded-full" aria-hidden />
                  {step}
                </span>
              ))}
            </div>
          </div>

          <div className="relative flex flex-col gap-3 border-t border-white/[0.06] bg-black/25 p-5 @4xl:border-t-0 @4xl:border-l">
            <div className="flex items-center justify-between gap-2">
              <span className={HEADER}>Your browser snapshot</span>
            </div>
            <div className="flex flex-wrap items-center gap-2.5">
              <div className="relative shrink-0 p-2.5">
                <CornerBrackets />
                <GlyphGhost />
              </div>
              <div className="min-w-44 flex-1">
                <div className="flex flex-wrap gap-x-2 gap-y-1.5">
                  {HASH_GROUPS.map((group) => (
                    <Bar key={group} className="h-3.5 w-[4.25rem]" />
                  ))}
                </div>
              </div>
            </div>
            {/* True while the scan runs, so it is plain text rather than a blank. */}
            <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-2">
              <span className="font-mono text-[9px] font-semibold tracking-widest text-white/45 uppercase">
                sha-256 · run 01 · computing locally
              </span>
            </div>
          </div>
        </div>

        {/* Instrument panel — the three verdict columns, eyebrows already in place. */}
        <div className="relative grid divide-y divide-white/[0.06] border-t border-white/[0.06] @4xl:grid-cols-3 @4xl:divide-x @4xl:divide-y-0">
          <div className="flex flex-col p-5">
            <span className={EYEBROW}>Exposure</span>
            <Bar className="mt-2.5 h-5 w-56 max-w-full" />
            <p className="mt-2.5 text-[13px] leading-relaxed text-white/65">
              Five high-signal surfaces trackers combine.
            </p>
            <ul className="mt-5 flex flex-wrap gap-1.5">
              {VECTORS.map(({ key, label, icon: Icon }) => (
                <li
                  key={key}
                  className="inline-flex items-center gap-1.5 rounded-md bg-white/[0.015] px-2 py-1.5 ring-1 ring-white/[0.04]"
                >
                  <Icon className="size-3.5 text-white/45" aria-hidden />
                  <span className="font-mono text-[9px] font-semibold tracking-widest text-white/45 uppercase">
                    {label}
                  </span>
                  {/* Sized like the outcome word that lands here ("read", "blocked"), so the
                   * chip keeps its width and the row never reflows on arrival. */}
                  <Bar className="h-2 w-5" />
                </li>
              ))}
            </ul>
            {/* Stands in for the column's "[ see all 81 properties ↓ ]" jump control. Each
             * column below carries the same trailing slot, which is what brings the three of
             * them to the heights the filled report has (measured: 192 / 173 / 209). */}
            <Bar className="mt-4 h-3.5 w-44 max-w-full" />
          </div>

          <div className="flex flex-col p-5">
            <span className={EYEBROW}>Defences</span>
            <Bar className="mt-2.5 h-5 w-48 max-w-full" />
            <Bar className="mt-3 h-3 w-60 max-w-full" />
            <div className="mt-5 flex items-end gap-1.5">
              {VECTORS.map(({ key }) => (
                <Bar key={key} className="h-8 w-1.5" />
              ))}
            </div>
            {/* The "of 5 observed as defended" readout under the numeral. */}
            <Bar className="mt-2.5 h-2.5 w-36 max-w-full" />
          </div>

          <div className="flex flex-col p-5">
            <span className={EYEBROW}>Consistency</span>
            <Bar className="mt-2.5 h-5 w-44 max-w-full" />
            <Bar className="mt-3 h-3 w-56 max-w-full" />
            <div className="mt-5 flex flex-col gap-2">
              <Bar className="h-3 w-40 max-w-full" />
              <Bar className="h-3 w-32 max-w-full" />
              <Bar className="h-3 w-36 max-w-full" />
            </div>
            <Bar className="mt-4 h-3.5 w-32 max-w-full" />
          </div>
        </div>
      </Card>

      {/* The two cards the report puts below the instrument. These hold page height rather than
       * predict content: the real cards are ~630px and ~4,280px here, but both depend on what
       * the scan finds (how many checks ran, how many properties were readable), so there is no
       * honest number to match. What matters is that the footer never rides up into view and
       * then gets shoved down — hence the viewport-relative floor on the inspector below. */}
      <Card className="gap-4 px-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className={HEADER}>Consistency checks</span>
          <Bar className="h-4 w-32" />
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          {CHECK_ROWS.map((row) => (
            <Bar key={row} className="h-9" />
          ))}
        </div>
      </Card>

      <Card className="gap-4 px-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className={HEADER}>All properties</span>
          <Bar className="h-7 w-44 max-w-full" />
        </div>
        <div className="grid min-h-[45svh] auto-rows-min gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {PROPERTY_ROWS.map((row) => (
            <Bar key={row} className="h-16" />
          ))}
        </div>
      </Card>
    </div>
  );
}
